import { takeEvery, select, put, call } from 'redux-saga/effects';
import {
  ADD_NOTIFICATION_SETTINGS,
  DELETE_NOTIFICATION_SETTINGS,
  MANAGE_NOTIFICATION_CHANNEL,
  MANAGE_NOTIFICATION_SETTINGS,
  GET_NOTIFICATION_SETTINGS,
  NOTIFY_TYPES,
} from '@control-front-end/common/constants/notifications';
import { RequestStatus } from '@control-front-end/common/constants';
import { DateUtils } from 'mw-style-react';
import { UPDATE_ACTOR_LIST } from '@control-front-end/common/constants/graphActors';
import api from '@control-front-end/common/sagas/api';
import { updateActorView } from './actorView';

function* updateActor(objId, data = null) {
  const actorView = yield select((state) => state.actorView);
  const { list } = yield select((state) => state.actorsList);
  const actor = list.find((i) => i.id === objId) || actorView;
  if (!actor) return;

  yield call(updateActorView, {
    payload: {
      actorData: {
        ...actor,
        notificationSettings: data,
      },
    },
  });

  if (!list.length) return;

  const copyList = list.slice();
  const index = list.findIndex((i) => i.id === objId);
  if (index === -1) return;
  const updatedEvent = { ...copyList[index], notificationSettings: data };
  copyList.splice(index, 1, updatedEvent);
  yield put({
    type: UPDATE_ACTOR_LIST.SUCCESS,
    payload: {
      list: copyList,
    },
  });
}

function* updateStream(type, objId, data = null) {
  const { notificationSettings } = yield select((state) => state.settings);
  const notification = [...(notificationSettings[type] ?? [])];

  // Find index of existing element
  const existingIndex = notification.findIndex((item) => item.objId === objId);

  if (data === null) {
    // Remove element if data === null
    if (existingIndex !== -1) {
      notification.splice(existingIndex, 1);
    }
  } else if (existingIndex !== -1) {
    // Replace existing element
    notification[existingIndex] = data;
  } else {
    // Add new element
    notification.push(data);
  }

  yield put({
    type: MANAGE_NOTIFICATION_SETTINGS.SUCCESS,
    payload: {
      notificationSettings: { ...notificationSettings, [type]: notification },
    },
  });
}

function* addNotificationSettings({ payload, callback }) {
  const { type = null, objId = null, snooze = null } = payload;

  if (!type) return;

  const activeAccountId = yield select(
    (state) => state.accounts.activeAccount.id
  );

  const body = {
    type,
    ...(objId != null && { objId }),
    ...(snooze != null && { snooze }),
  };

  const { result, data } = yield call(api, {
    method: 'post',
    url: `/notifications/${activeAccountId}`,
    body,
  });

  if (result !== RequestStatus.SUCCESS) return;

  if (type === NOTIFY_TYPES.actor) {
    yield call(updateActor, objId, data.data);
  } else if (type === NOTIFY_TYPES.stream || type === NOTIFY_TYPES.sysStream) {
    yield call(updateStream, type, objId, data.data);
  } else {
    const { notificationSettings } = yield select((state) => state.settings);

    const newNotificationSettings = {
      ...notificationSettings,
      [data.data.type]: {
        snooze: data.data.snooze,
        enabled: false,
      },
    };
    yield put({
      type: MANAGE_NOTIFICATION_SETTINGS.SUCCESS,
      payload: { notificationSettings: newNotificationSettings },
    });
  }

  if (callback) callback(data.data);
  return data.data;
}

function* deleteNotificationSettings({ payload, callback }) {
  const { type = null, objId = null } = payload;

  if (!type) return;

  const activeAccountId = yield select(
    (state) => state.accounts.activeAccount.id
  );

  const body = {
    type,
    ...(objId != null && { objId }),
  };

  const { result, data } = yield call(api, {
    method: 'delete',
    url: `/notifications/${activeAccountId}`,
    body,
  });

  if (result !== RequestStatus.SUCCESS) return;

  if (type === NOTIFY_TYPES.actor) {
    yield call(updateActor, objId, null);
  } else if (type === NOTIFY_TYPES.stream || type === NOTIFY_TYPES.sysStream) {
    yield call(updateStream, type, objId, null);
  } else {
    const { notificationSettings } = yield select((state) => state.settings);

    const newNotificationSettings = {
      ...notificationSettings,
      [type]: null,
    };
    yield put({
      type: MANAGE_NOTIFICATION_SETTINGS.SUCCESS,
      payload: { notificationSettings: newNotificationSettings },
    });
  }
  if (callback) callback(data.data);
  return data.data;
}

function* manageNotificationChannel({ payload, callback }) {
  const { channel, enable } = payload;

  const activeAccountId = yield select(
    (state) => state.accounts.activeAccount.id
  );

  const { result, data } = yield call(api, {
    method: 'post',
    url: `/notifications/channel/${activeAccountId}/${channel}`,
    body: { enable },
  });

  if (result !== RequestStatus.SUCCESS) return;

  yield put({
    type: MANAGE_NOTIFICATION_SETTINGS.SUCCESS,
    payload: { channelSettings: data.data },
  });

  if (callback) callback(data.data);
  return data.data;
}

function* getNotificationSettings({ callback }) {
  const activeAccountId = yield select(
    (state) => state.accounts.activeAccount.id
  );

  const { result, data } = yield call(api, {
    method: 'get',
    url: `/notifications/${activeAccountId}`,
  });

  if (result !== RequestStatus.SUCCESS) return;

  const { notificationSettings, channelSettings } = data.data;
  const timeNow = DateUtils.unixtime();

  for (const key in notificationSettings) {
    const notifications = notificationSettings[key];
    const filteredNotifications = notifications.filter(
      (item) =>
        item.snooze === null || item.createdAt + item.snooze * 60 >= timeNow
    );
    notificationSettings[key] = filteredNotifications.length
      ? filteredNotifications
      : null;
  }

  yield put({
    type: MANAGE_NOTIFICATION_SETTINGS.SUCCESS,
    payload: { channelSettings, notificationSettings },
  });

  if (callback) callback(data.data);
  return data.data;
}

function* notificationsSaga() {
  yield takeEvery(ADD_NOTIFICATION_SETTINGS.REQUEST, addNotificationSettings);
  yield takeEvery(
    DELETE_NOTIFICATION_SETTINGS.REQUEST,
    deleteNotificationSettings
  );
  yield takeEvery(
    MANAGE_NOTIFICATION_CHANNEL.REQUEST,
    manageNotificationChannel
  );
  yield takeEvery(GET_NOTIFICATION_SETTINGS.REQUEST, getNotificationSettings);
}

export default notificationsSaga;
